'use client'

import { useSearchParams, type ReadonlyURLSearchParams } from 'next/navigation'
import { useMemo } from 'react'
import type { SWRConfiguration } from 'swr'
import useSWRImmutable from 'swr/immutable'

import { useSession } from '@/legacy-src/contexts/session-context'

import { useApis } from '../contexts/api-context'
import { SERVICES_TYPE } from '../redux/DiagnosisRedux'
import { type ServiceType } from '../types/GeneralStateType'

const parseServiceIdFromQuery = (searchParams: ReadonlyURLSearchParams | null) => {
  const queryServiceParam = searchParams?.get('service')
  if (!queryServiceParam) return undefined

  const parsed = Number(queryServiceParam)
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) return undefined

  return parsed
}

const useServices = (options?: SWRConfiguration) => {
  const { api } = useApis()
  const searchParams = useSearchParams()
  const { isLoggedIn } = useSession()

  const { data: servicesPublic } = useSWRImmutable(
    'useServices',
    async () => {
      const res = await api.getAllServices({ scope: 'therapist' })
      return res.data as ServiceType[]
    },
    options,
  )
  const { data: servicesPrivate, mutate: refreshPrivateServices } = useSWRImmutable(
    isLoggedIn ? 'useServicesPrivate' : null,
    async () => {
      const res = await api.getAllServicesPrivate({ scope: 'therapist' })
      return res.data as ServiceType[]
    },
    options,
  )

  const services = servicesPrivate || servicesPublic

  const defaultServiceId = services?.[0]?.id
  const queryServiceId = parseServiceIdFromQuery(searchParams)
  const selectedServiceId = queryServiceId ?? defaultServiceId
  const selectedService = services?.find((service) => service.id === selectedServiceId)
  const isCounselling = !!(selectedService && selectedService.type === SERVICES_TYPE.COUNSELLING)
  const visibleServices = useMemo(() => services?.filter((service) => service.visible), [services])

  return {
    services,
    servicesPrivate,
    isCounselling,
    isLoadingServices: !services,
    defaultServiceId,
    selectedService,
    selectedServiceId,
    visibleServices,
    refreshPrivateServices,
    queryServiceId,
  }
}

export default useServices
